<script setup lang="ts">
import { useStore } from 'vuex';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import Notification from './notifications/notification.vue';

const store = useStore();

const minimizeHeader = ref(false);
const _ok = ref(null);
const _undo = ref(null);
const _ok_title = ref('Ok');
const _undo_title = ref('Undo');
const _emoji = ref('');
const _title = ref('');
const _ok_action = ref<() => void>();
const _undo_action = ref<() => void>();
const _link = ref('');
const _link_title = ref('View more');

const type = ref(store.state.notificationTimer?.type());
const showNotification = computed(() => store.state.showNotification);

function mouseover(event: MouseEvent) {
  store.dispatch('notification_hover');
}

function mouseleave(event: MouseEvent) {
  store.dispatch('notification_leave');
}

function onShowNotification(e: Event) {
  const event = e as CustomEvent;
  const type_event = event.detail.type;
  type.value = type_event;
  const { emoji, title, ok_action, undo_action, ok_title, undo_title, link, link_title } = event.detail.params;
  _emoji.value = emoji;
  _title.value = title;
  _ok_title.value = ok_title ? ok_title : 'Ok';
  _undo_title.value = undo_title ? undo_title : 'Undo';
  _link.value = link;
  _link_title.value = link_title ?? 'View more';

  if (ok_action) {
    _ok_action.value = () => {
      ok_action();
      store.dispatch('ok_click');
    };
  } else {
    _ok_action.value = undefined;
  }

  if (undo_action) {
    _undo_action.value = () => {
      undo_action();
      store.dispatch('close_click');
    };
  } else {
    _undo_action.value = undefined;
  }

  store.dispatch(type_event, event.detail.params.data);
}

function closeCurrentNotification(e: Event) {
  store.dispatch('close_click');
}

// function onScroll() {
//   minimizeHeader.value = window.scrollY > 20
// }
// watch(minimizeHeader, (newVal) => {
//   const header = document.querySelector(".header-container")
//   if (newVal > 0) {
//     header?.classList.add("minimize")
//   } else {
//     header?.classList.remove("minimize")
//   }
// })

onMounted(() => {
  // window.addEventListener("scroll", onScroll)
  window.addEventListener('showNotification', onShowNotification);
  window.addEventListener('closeCurrentNotification', closeCurrentNotification);
});

onUnmounted(() => {
  // window.removeEventListener("scroll", onScroll)
  window.removeEventListener('showNotification', onShowNotification);
  window.removeEventListener('closeCurrentNotification', closeCurrentNotification);
});
</script>

<template>
  <div
    v-if="showNotification"
    class="notification-banner pointer-events-none fixed left-[0px] top-[25px] z-[5003] flex w-full justify-center transition-[top] duration-150 ease-linear max-tablet:top-s"
    :class="{
      'top-[5px]': minimizeHeader,
      copy: type === 'copy_draft',
      'minute-delay': type === 'draft_comment_disabled' || type === 'error_type',
      second: type === 'fast_type',
    }"
  >
    <notification
      :icon="_emoji"
      :title="_title"
      :ok="_ok_action"
      :undo="_undo_action"
      :ok_title="_ok_title"
      :undo_title="_undo_title"
      :link="_link"
      :link_title="_link_title"
      @mouseover="mouseover"
      @mouseleave="mouseleave"
    />
  </div>
</template>

<style scoped lang="scss">
.notification-banner {
  animation: fadeOut 4s;
  animation-delay: 4s;
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;

  &.second {
    animation-delay: 0s;
  }

  &.copy {
    animation-delay: 2s;
  }

  &.minute-delay {
    animation-delay: 60s;
  }

  &:hover {
    animation: none;
  }
}

.notification-enter-active {
  transition: opacity 0.5s ease;
}

.notification-leave-active {
  transition: opacity 0s ease;
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}
</style>
